<template>
  <div :class="`prize-banner h-full flex flex-col justify-center prize-banner-${variant}`">
    <div class="container flex flex-row gap-200">
      <div class="flex flex-col">
        <h2 class="headline paragraph-small-text text-secondary-secondary">
          {{ headline }}
        </h2>

        <div :class="`flex headline-gap-${variant}`">
          <h3 class="prize">{{ jackpot }}</h3>
          <h2
            v-if="drawLabel"
            :class="`draw draw-${variant} paragraph-small-text text-secondary-secondary`"
          >
            {{ drawLabel }}
          </h2>
        </div>

        <slot />
      </div>

      <template v-if="odds">
        <div class="flex flex-col justify-center">
          <div class="separator" />
        </div>

        <div class="flex flex-col">
          <h2 class="headline paragraph-small-text text-secondary-secondary">
            {{ $t('pricebanner.labels.odds') }}
          </h2>

          <div class="flex gap-050">
            <h3 class="prize">{{ odds }}</h3>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
type Props = {
  drawLabel?: string
  headline: string
  variant?: 'banner' | 'modal'
  visibleJackpot?: Money | undefined
  odds?: string
}

const { headline, variant = 'modal', visibleJackpot } = defineProps<Props>()
const { formatPrizeMoney } = usePrizeMoneyFormatter()
const { t } = useI18n()

const jackpot = computed(() =>
  visibleJackpot ? formatPrizeMoney(visibleJackpot) : t('pricebanner.labels.jackpotmissing'),
)
</script>

<style lang="scss" scoped>
.prize-banner {
  position: relative;
  z-index: 0;
  overflow: hidden;

  .container {
    z-index: 1;
  }

  .draw {
    align-self: end;

    &-banner {
      line-height: 0.875rem;
    }

    &-modal {
      line-height: 1.375rem;
    }
  }

  .headline {
    margin-bottom: 0.0625rem;

    &-gap {
      &-banner {
        gap: 0.25rem;
      }

      &-modal {
        gap: 0.375rem;
      }
    }
  }

  .prize {
    height: auto;
    padding-left: 0.075rem;
    text-shadow:
      0 0.025rem token('color/yellow/800'),
      0 0.05rem token('color/yellow/700'),
      0 0.075rem token('color/yellow/600');
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      21.16% 152.97% at 15.08% 40.76%,
      token('color/background/accent/win/win-bold') 0%,
      rgb(255, 248, 181, 0%) 100%
    );
    pointer-events: none;
  }

  &-banner {
    gap: token('size/space/gap/025');
    background: token('color/background/accent/win/win') url('./assets/prize-banner-small.svg')
      no-repeat right center;

    .prize {
      @include token-paragraph-bold;

      line-height: token('font/caption/bold/line-height');
    }
  }

  &-modal {
    background: token('color/background/accent/win/win') url('./assets/prize-banner-large.svg')
      no-repeat right center;

    .prize {
      @include token-headline-h3-headline;
    }
  }

  .separator {
    width: 0.0625rem;
    height: 1.5rem;
    border-radius: token('size/radius/xs');
    background-color: token('color/yellow/800');
  }
}
</style>
